import api from '@/lib/httpClient';

export interface ClientPreferenceOption {
  id: string;
  name: string;
  value: string;
  parentId?: string;
}

export interface ClientPreferenceOptions {
  allergies: ClientPreferenceOption[];
  foodIntolerances: ClientPreferenceOption[];
  dietaryPreferences: ClientPreferenceOption[];
  cuisineTypes: ClientPreferenceOption[];
}

export interface ClientPreferenceOptionsResponse {
  success?: boolean;
  message?: string;
  data?: ClientPreferenceOptions | null;
}

const asOption = (raw: unknown): ClientPreferenceOption | null => {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const value = String(record.value ?? record.id ?? '').trim();
  const name = String(record.name ?? record.label ?? value).trim();
  const id = String(record.id ?? value).trim();
  const parentId = String(record.parentId ?? record.parentValue ?? '').trim();
  if (!value || !name) return null;
  return { id: id || value, name, value, ...(parentId ? { parentId } : {}) };
};

const asOptionList = (raw: unknown): ClientPreferenceOption[] => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const options: ClientPreferenceOption[] = [];
  for (const item of raw) {
    const option = asOption(item);
    if (!option) continue;
    const key = option.value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    options.push(option);
  }
  return options;
};

const normalizePreferenceOptions = (raw: unknown): ClientPreferenceOptions => {
  const root =
    raw && typeof raw === 'object' && 'data' in (raw as object)
      ? (raw as { data: unknown }).data
      : raw;
  const record =
    root && typeof root === 'object' ? (root as Record<string, unknown>) : {};

  return {
    allergies: asOptionList(record.allergies),
    foodIntolerances: asOptionList(
      record.foodIntolerances ?? record.intolerances ?? record.food_intolerances,
    ),
    dietaryPreferences: asOptionList(
      record.dietaryPreferences ?? record.preferences ?? record.dietary_preferences,
    ),
    cuisineTypes: asOptionList(
      record.cuisineTypes ?? record.cuisines ?? record.cuisine_types,
    ),
  };
};

/** GET /clients/preference-options — allergies, intolerances, preferences, cuisine types. */
export const getClientPreferenceOptions = async (): Promise<ClientPreferenceOptions> => {
  const response = await api.get<ClientPreferenceOptionsResponse | ClientPreferenceOptions>(
    '/clients/preference-options',
  );
  return normalizePreferenceOptions(response.data);
};
